import 'core-js/modules/es.array.includes';
import 'core-js/modules/es.array.join';
import 'core-js/modules/es.number.constructor';
import 'core-js/modules/es.number.parse-int';
import 'core-js/modules/es.object.assign';
import 'core-js/modules/es.object.keys';
import 'core-js/modules/es.object.values';
import 'core-js/modules/es.regexp.constructor';
import 'core-js/modules/es.regexp.exec';
import 'core-js/modules/es.regexp.to-string';
import 'core-js/modules/es.string.includes';
import 'core-js/modules/es.string.split';
import { isWorldReadyInstance } from '@paypalcorp/worldready-core';
import { assertType, assert, assertRange } from 'yaassertion';
import RelativeTime from '@paypalcorp/relative-time/dist/relative-time';

function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}

var _excluded = ["unit"];
var getMissingMetadataError = function getMissingMetadataError(path) {
  var message = "Missing WorldReady metadata, please refer to https://github.paypal.com/pages/Globalization-R/worldready-js/docs/about-load-worldready-data.html";
  if (path) {
    message += "\nPath: " + path;
  }
  return new Error(message);
};
function globalizeToWorldReadyError(error) {
  if (error.code === "E_MISSING_CLDR") {
    return getMissingMetadataError(error.stack);
  }
  return new Error(["Oops, internal error", error.message, error.stack].join("\n"));
}
function validateCldr(path, value, options) {
  if (options === void 0) {
    options = {
      skip: []
    };
  }
  var skipBoolean = options.skip.some(function (regex) {
    return regex.test(path);
  });
  if (skipBoolean) {
    return;
  }
  if (value === undefined) {
    throw getMissingMetadataError(path);
  }
}
var ISO8601 = "iso8601";
var RELATIVE_TIME_UNITS = {
  BEST_FIT: "bestFit",
  SECOND: "second",
  MINUTE: "minute",
  HOUR: "hour",
  DAY: "day",
  MONTH: "month",
  YEAR: "year"
};
var RELATIVE_TIME_FORMATTERS_STYLES = {
  LONG: "long",
  NARROW: "narrow",
  SHORT: "short"
};
var RELATIVE_TIME_STYLES = {
  DURATION_NARROW: "duration-narrow",
  DURATION_LONG: "duration-long",
  DURATION_SHORT: "duration-short"
};
var RELATIVE_TIME_ERRORS = {
  INVALID_INPUT: {
    code: "INVALID_INPUT",
    message: "Please provide a valid input for parsing ISO 8601 duration."
  }
};
var matchStringWithRegex = function matchStringWithRegex(value, regex) {
  if (value === void 0) {
    value = "";
  }
  if (regex === void 0) {
    regex = "";
  }
  var regexExp = new RegExp(regex);
  return regexExp.test(value);
};
var DURATION_CODES = {
  PERIOD: "P",
  YEARS: "Y",
  MONTHS: "M",
  WEEKS: "W",
  DAYS: "D",
  TIME: "T",
  HOURS: "H",
  MINUTES: "M",
  SECONDS: "S"
};

/**
 * Function that will format ISO 8601 duration string into relative time format
 * E.g., for input "P3D", output will be "3 days" (for locale en-US)
 * @param {String} duration - an iso 8601 duration string
 * @param {Object} options - an object that can contain any properties for additional filtering
 * For now it contains the regex for validating the input duration string as per regex identified by ppaas
 * https://github.paypal.com/ApiSpecifications-R/CommonComponentsSpecification/blob/master/v4/schema/json/openapi-2.0/time_duration.json#L6
 * @returns {String}
 */
var formatISO8601Duration = function formatISO8601Duration(duration, options) {
  if (duration === void 0) {
    duration = "";
  }
  if (options === void 0) {
    options = {};
  }
  try {
    if (duration === "" || typeof options !== "object") {
      return RELATIVE_TIME_ERRORS.INVALID_INPUT;
    }

    /**
     * 1. Validate the input string using regex
     * 2. Convert duration to a date format object
     * 3. Return the date object
     */

    var result = null;

    // 1.
    validateISO8601Duration(duration, options.validations.default.iso8601.pattern.default || "");
    var parsedDuration = parseISO8601Duration(duration);
    var futureDate = addTimeToDate(new Date(), parsedDuration);

    // 2.
    result = futureDate;
    return result;
  } catch (error) {
    throw new Error("" + error.message);
  }
};

/**
 * Helper function to validate the iso8601 duration string
 * @param {String} duration - an iso 8601 duration string
 * @param {String} validationRegex - regex pattern against which the duration string should be tested
 * @returns {Object} an object containing error code and message. If there are no errors, then an empty object will be returned.
 */
var validateISO8601Duration = function validateISO8601Duration(duration, validationRegex) {
  if (duration === void 0) {
    duration = "";
  }
  if (validationRegex === void 0) {
    validationRegex = "";
  }
  if (duration === "" || !matchStringWithRegex(duration, validationRegex)) {
    throw new Error(RELATIVE_TIME_ERRORS.INVALID_INPUT.code + ". " + RELATIVE_TIME_ERRORS.INVALID_INPUT.message);
  }
  return;
};

/**
 * Helper function to convert a valid ISO 8601 duration string into an object specifying each element separately
 * NOTE: This function is not used right now but can be used in DateTimeFormat.parse API to parse
 * each element of ISO 8601 duration separately.
 * @param {String} duration - string in the ISO 8601 format
 * @returns {Object} in the following format:
 * // For an input "P14Y10M3DT18H13M15S", output will be
 * {
 *    years: 14,
 *    months: 10,
 *    days: 3,
 *    hours: 18,
 *    minutes: 13,
 *    seconds: 15
 * }
 */
var parseISO8601Duration = function parseISO8601Duration(duration) {
  if (duration === void 0) {
    duration = "";
  }
  duration = duration.toLocaleUpperCase();
  var _duration$split = duration.split(DURATION_CODES.TIME),
    period = _duration$split[0],
    time = _duration$split[1];
  // Remove "P" from the start of period string
  period = period.substring(1);
  var parsedPeriod = parseISO8601Period(period);
  var parsedTime = parseISO8601Time(time);
  var result = Object.assign({}, parsedPeriod && Object.assign({}, parsedPeriod), {}, parsedTime && Object.assign({}, parsedTime));
  return result;
};

/**
 * Helper function to parse the period value from iso 8601 duration
 * @param {String} period
 * @returns {Object} in the following format:
 * For an input "3Y1D". output will be:
 * {
 *    years: 3,
 *    days: 1
 * }
 */
var parseISO8601Period = function parseISO8601Period(period) {
  if (period === void 0) {
    period = "";
  }
  if (period === "") {
    return null;
  }
  var years = null,
    months = null,
    weeks = null,
    days = null,
    rest = period;
  if (rest.includes(DURATION_CODES.YEARS)) {
    // Parse years into number
    var _rest$split = rest.split(DURATION_CODES.YEARS);
    years = _rest$split[0];
    rest = _rest$split[1];
    years = Number.parseInt(years);
  }
  if (rest && rest.includes(DURATION_CODES.MONTHS)) {
    // Parse months into a number
    var _rest$split2 = rest.split(DURATION_CODES.MONTHS);
    months = _rest$split2[0];
    rest = _rest$split2[1];
    months = Number.parseInt(months);
  }
  if (rest && rest.includes(DURATION_CODES.WEEKS)) {
    // Parse weeks into a number
    var _rest$split3 = rest.split(DURATION_CODES.WEEKS);
    weeks = _rest$split3[0];
    rest = _rest$split3[1];
    weeks = Number.parseInt(weeks);
  }
  if (rest && rest.includes(DURATION_CODES.DAYS)) {
    // Parse days into number
    var _rest$split4 = rest.split(DURATION_CODES.DAYS);
    days = _rest$split4[0];
    rest = _rest$split4[1];
    days = Number.parseInt(days);
  }
  return {
    years: years && years >= 0 ? years : undefined,
    months: months && months >= 0 ? months : undefined,
    weeks: weeks && weeks >= 0 ? weeks : undefined,
    days: days && days >= 0 ? days : undefined
  };
};

/**
 * Helper function to parse the time value from iso 8601 duration
 * @param {String} time
 * @returns {Object} in the following format:
 * For an input "3H2M". output will be:
 * {
 *    hours: 3,
 *    minutes: 1
 * }
 */
var parseISO8601Time = function parseISO8601Time(time) {
  if (time === void 0) {
    time = "";
  }
  if (time === "") {
    return null;
  }
  var hours = null,
    minutes = null,
    seconds = null,
    rest = time;
  if (rest.includes(DURATION_CODES.HOURS)) {
    // Parse hours into number
    var _rest$split5 = rest.split(DURATION_CODES.HOURS);
    hours = _rest$split5[0];
    rest = _rest$split5[1];
    hours = Number.parseInt(hours);
  }
  if (rest && rest.includes(DURATION_CODES.MINUTES)) {
    // Parse minutes into a number
    var _rest$split6 = rest.split(DURATION_CODES.MINUTES);
    minutes = _rest$split6[0];
    rest = _rest$split6[1];
    minutes = Number.parseInt(minutes);
  }
  if (rest && rest.includes(DURATION_CODES.SECONDS)) {
    // Parse seconds into number
    var _rest$split7 = rest.split(DURATION_CODES.SECONDS);
    seconds = _rest$split7[0];
    rest = _rest$split7[1];
    seconds = Number.parseInt(seconds);
  }
  return {
    hours: hours && hours >= 0 ? hours : undefined,
    minutes: minutes && minutes >= 0 ? minutes : undefined,
    seconds: seconds && seconds >= 0 ? seconds : undefined
  };
};

/**
 * Add time to a date object
 * @param {Date} date - date object to which you want to add time to
 * @param {Object} timeToAdd - object with the following structure
 *  {
 *    years: n,
 *    months: n,
 *    weeks: n,
 *    hours: n,
 *    minutes: n,
 *    seconds: n
 *  }
 * Here n is the integer value
 * @returns {Date} new Date object after adding the time mentioned in timeToAdd object
 */
function addTimeToDate(date, timeToAdd) {
  if (date === void 0) {
    date = new Date();
  }
  if (timeToAdd === void 0) {
    timeToAdd = {};
  }
  if (!Object.keys(timeToAdd).length) {
    return date;
  }
  var _timeToAdd = timeToAdd,
    years = _timeToAdd.years,
    months = _timeToAdd.months,
    weeks = _timeToAdd.weeks,
    days = _timeToAdd.days,
    hours = _timeToAdd.hours,
    minutes = _timeToAdd.minutes,
    seconds = _timeToAdd.seconds;
  var result = date;
  years && result.setFullYear(result.getFullYear() + years);
  months && result.setMonth(result.getMonth() + months);
  if (weeks) {
    days = weeks * 7 + (days || 0);
  }
  days && result.setDate(result.getDate() + days);
  hours && result.setHours(result.getHours() + hours);
  minutes && result.setMinutes(result.getMinutes() + minutes);
  seconds && result.setSeconds(result.getSeconds() + seconds);
  return result;
}
var ENVIRONMENT = {
  PRODUCTION: "production"
};
RelativeTime.threshold = {
  month: 11,
  // at least 11 months before using year.
  day: 26,
  // at least 26 days before using month.
  hour: 22,
  // at least 22 hours before using day.
  minute: 45,
  // at least 45 minutes before using hour.
  second: 45 // at least 45 seconds before using minute.
};
function getTimeInDurationNarrowStyle(formatter, value) {
  return formatter(Math.abs(value));
}
function _initializeFormattersForStyles(globalize, style) {
  var formatter = {
    second: globalize.unitFormatter(RELATIVE_TIME_UNITS.SECOND, {
      form: style
    }),
    minute: globalize.unitFormatter(RELATIVE_TIME_UNITS.MINUTE, {
      form: style
    }),
    hour: globalize.unitFormatter(RELATIVE_TIME_UNITS.HOUR, {
      form: style
    }),
    day: globalize.unitFormatter(RELATIVE_TIME_UNITS.DAY, {
      form: style
    }),
    month: globalize.unitFormatter(RELATIVE_TIME_UNITS.MONTH, {
      form: style
    }),
    year: globalize.unitFormatter(RELATIVE_TIME_UNITS.YEAR, {
      form: style
    })
  };
  return {
    second: function second(value) {
      return getTimeInDurationNarrowStyle(formatter.second, value);
    },
    minute: function minute(value) {
      return getTimeInDurationNarrowStyle(formatter.minute, value);
    },
    hour: function hour(value) {
      return getTimeInDurationNarrowStyle(formatter.hour, value);
    },
    day: function day(value) {
      return getTimeInDurationNarrowStyle(formatter.day, value);
    },
    month: function month(value) {
      return getTimeInDurationNarrowStyle(formatter.month, value);
    },
    year: function year(value) {
      return getTimeInDurationNarrowStyle(formatter.year, value);
    }
  };
}
var RelativeTimeFormat = /*#__PURE__*/function () {
  function RelativeTimeFormat(worldReady, options) {
    // Error Check
    if (process.env.NODE_ENV !== ENVIRONMENT.PRODUCTION) {
      assert(isWorldReadyInstance(worldReady), "WorldReady object missing.");
      assertType(options, "options", ["plain object"]);
    }
    var _options$unit = options.unit,
      unit = _options$unit === void 0 ? "bestFit" : _options$unit,
      otherProps = _objectWithoutPropertiesLoose(options, _excluded);

    // Error Checks
    if (process.env.NODE_ENV !== ENVIRONMENT.PRODUCTION) {
      if (otherProps) {
        assertType(otherProps.timeZone, "options.timeZone", ["string"]);
      }
      assertType(unit, "options.unit", ["string"]);
      var unitValues = Object.values(RELATIVE_TIME_UNITS);
      assertRange(unit, unitValues, "Invalid `options.unit`: `{{unit}}`. Please, read  https://github.paypal.com/pages/Globalization-R/@paypalcorp/griffin-3/docs/relative-time-api.html#optionsunit", {
        unit: unit
      });
    }
    var globalize = worldReady.globalize;
    var cldr = globalize.cldr;
    unit = unit === RELATIVE_TIME_UNITS.BEST_FIT ? "best-fit" : unit;
    this.options = Object.assign({
      unit: unit
    }, otherProps);
    cldr.on("get", validateCldr);
    try {
      this.timeZoneData = getTimeZoneData(cldr.get("globalize-iana/zoneData"), options.timeZone);
      this.validations = cldr.get(["supplemental", "dateValidations"]);
    } finally {
      cldr.off("get", validateCldr);
    }

    // Throw an error if metadata for options.timeZone was not found
    if (!this.timeZoneData) {
      throw new Error("Missing required timezone metadata. Make sure that you are exporting metadata for \"" + options.timeZone + "\" using WorldReady.export API.");
    }
    this.relativeTimeFormatter = function (date) {
      initializeRelativeTimeFormatters();
      return new RelativeTime(globalize, options).format(date, {
        timeZoneData: this.timeZoneData,
        unit: this.options.unit
      });
    };
  }
  var _proto = RelativeTimeFormat.prototype;
  _proto.format = function format(date, options) {
    if (options === void 0) {
      options = {};
    }
    if (process.env.NODE_ENV !== ENVIRONMENT.PRODUCTION) {
      if (options.type === ISO8601) {
        assertType(date, "date", ["string"]);
      } else {
        assertType(date, "date", ["date"]);
      }
    }
    try {
      if (options.type === ISO8601) {
        return getRelativeTimeFormatForISO8601Duration.call(this, date, {
          validations: this.validations
        });
      }
      return this.relativeTimeFormatter(date);
    } catch (error) {
      throw globalizeToWorldReadyError(error);
    }
  };
  return RelativeTimeFormat;
}();
function initializeRelativeTimeFormatters() {
  RelativeTime.initializeFormatters = function (globalize, _ref) {
    var style = _ref.style;
    if (style === RELATIVE_TIME_STYLES.DURATION_NARROW) {
      return _initializeFormattersForStyles(globalize, RELATIVE_TIME_FORMATTERS_STYLES.NARROW);
    } else if (style === RELATIVE_TIME_STYLES.DURATION_LONG) {
      return _initializeFormattersForStyles(globalize, RELATIVE_TIME_FORMATTERS_STYLES.LONG);
    } else if (style === RELATIVE_TIME_STYLES.DURATION_SHORT) {
      return _initializeFormattersForStyles(globalize, RELATIVE_TIME_FORMATTERS_STYLES.SHORT);
    }
    return {
      second: globalize.relativeTimeFormatter(RELATIVE_TIME_UNITS.SECOND),
      minute: globalize.relativeTimeFormatter(RELATIVE_TIME_UNITS.MINUTE),
      hour: globalize.relativeTimeFormatter(RELATIVE_TIME_UNITS.HOUR),
      day: globalize.relativeTimeFormatter(RELATIVE_TIME_UNITS.DAY),
      month: globalize.relativeTimeFormatter(RELATIVE_TIME_UNITS.MONTH),
      year: globalize.relativeTimeFormatter(RELATIVE_TIME_UNITS.YEAR)
    };
  };
}
function getTimeZoneData(data, timeZone) {
  // Traverse IANA time zone *data* given a timeZone string.
  // - Break timeZone string into parts like ["America", "Los_Angeles"]
  // - Iterate until the whole timeZone path is traversed (immediately stop in case any node can't
  //   be traversed).
  // - Return the last node in case data was found, or null otherwise.
  return timeZone.split("/").every(function (node) {
    data = data[node];
    return !!data;
  }) ? data : null;
}
function getRelativeTimeFormatForISO8601Duration(duration, options) {
  if (duration === void 0) {
    duration = "";
  }
  if (options === void 0) {
    options = {};
  }
  // Check for invalid inputs
  if (duration === "") {
    throw new Error(RELATIVE_TIME_ERRORS.INVALID_INPUT.code + ". " + RELATIVE_TIME_ERRORS.INVALID_INPUT.message);
  }

  // Parse duration string into date object
  var date = formatISO8601Duration(duration, {
    validations: options.validations
  });

  // Format date object as relative time and return the result
  return this.relativeTimeFormatter(date);
}

export { RelativeTimeFormat };
